// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { z } from 'zod';
import { isDefined, isUndefined } from '@shared/utils';

export const sustainabilitySchema = z
  .object({
    q_sustainability_preferences: z.enum([
      'no_sustainability_preferences',
      'neutral_sustainability',
      'delegate_sustainability',
      'determine_sustainability_criteria',
    ]),
    q_sustainability_percentage: z.number().min(0).max(100).optional(),
    q_sustainability_criteria: z
      .array(z.enum(['eu_taxonomy', 'sfdr', 'pias']))
      .optional(),
    q_taxonomy_percentage: z
      .enum(['taxonomy_0', 'taxonomy_5', 'taxonomy_25', 'taxonomy_50'])
      .optional(),
    q_sfdr_percentage: z
      .enum(['sfdr_0', 'sfdr_25', 'sfdr_50', 'sfdr_100'])
      .optional(),
    q_pias_preference: z
      .enum(['pias_consider', 'pias_avoid', 'pias_exclude'])
      .optional(),
  })
  .refine(
    data => {
      if (
        data.q_sustainability_preferences !== 'determine_sustainability_criteria'
      ) {
        return true;
      }
      return isDefined(data.q_sustainability_percentage);
    },
    {
      message: 'Sustainability percentage is required when determining criteria',
      path: ['q_sustainability_percentage'],
    },
  )
  .refine(
    data => {
      if (!data.q_sustainability_criteria?.includes('eu_taxonomy')) {
        return true;
      }
      return !isUndefined(data.q_taxonomy_percentage);
    },
    {
      message: 'Taxonomy percentage is required when EU Taxonomy is selected',
      path: ['q_taxonomy_percentage'],
    },
  )
  .refine(
    data => {
      if (!data.q_sustainability_criteria?.includes('sfdr')) {
        return true;
      }
      return !isUndefined(data.q_sfdr_percentage);
    },
    {
      message: 'SFDR percentage is required when SFDR is selected',
      path: ['q_sfdr_percentage'],
    },
  )
  .refine(
    data => {
      if (!data.q_sustainability_criteria?.includes('pias')) {
        return true;
      }
      return !isUndefined(data.q_pias_preference);
    },
    {
      message: 'PIAS preference is required when PIAS is selected',
      path: ['q_pias_preference'],
    },
  );

export type SustainabilityFormData = z.infer<typeof sustainabilitySchema>;
